import { Box, IconButton, Stack, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useGlobalStore } from "@/stores/global";
import { useEffect, useMemo, useRef, useState } from "react";
import useRights from "@/shared/api/useRights";
import { Navigate } from "react-router-dom";
import Slot, { useAvailableSlots } from "./Slot";
import { TopBar } from "@/shared/structure/TopBar";

const SLOT_MIN_WIDTH = 400;
const SLOT_MIN_HEIGHT = 400;
const GAP_PX = 16;

function computeMaxGrid(size: { width: number; height: number }) {
  const maxCols = Math.max(1, Math.floor((size.width + GAP_PX) / (SLOT_MIN_WIDTH + GAP_PX)));
  const maxRows = Math.max(1, Math.floor((size.height + GAP_PX) / (SLOT_MIN_HEIGHT + GAP_PX)));
  return { maxRows, maxCols };
}

export default function Startpage() {
  const setPageTitle = useGlobalStore((state) => state.setPageTitle);
  const passwordChangeNecessary = useGlobalStore((state) => state.passwordChangeNecessary);

  useEffect(() => setPageTitle("Start"), [setPageTitle]);

  const availablePanels = useAvailableSlots().length;

  const rights = useRights();
  if (passwordChangeNecessary) return <Navigate replace to="/password" />;
  if ("SUPPLIER_VIEW" in rights) return <Navigate replace to="/plants" />;

  const boxRef = useRef<HTMLDivElement | null>(null);
  const [{ maxRows, maxCols }, setMaxGrid] = useState(() => ({ maxRows: 1, maxCols: 1 }));

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const { width, height } = entry.contentRect;
      const next = computeMaxGrid({ width, height });

      setMaxGrid((prev) => {
        if (prev.maxRows === next.maxRows && prev.maxCols === next.maxCols) return prev;
        return next;
      });
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // page state (0-based)
  const [page, setPage] = useState(0);

  const capacity = useMemo(() => maxRows * maxCols, [maxRows, maxCols]);

  const pageCount = useMemo(() => {
    const safeAvailable = Math.max(0, availablePanels ?? 0);
    return Math.max(1, Math.ceil(safeAvailable / Math.max(1, capacity)));
  }, [availablePanels, capacity]);

  // clamp page when capacity/availablePanels changes (e.g. resize)
  useEffect(() => {
    setPage((p) => Math.min(p, pageCount - 1));
  }, [pageCount]);

  const { startIndex, renderCount, cols, rows } = useMemo(() => {
    const safeAvailable = Math.max(0, availablePanels ?? 0);
    const safeCapacity = Math.max(1, capacity);

    const startIndex = page * safeCapacity;
    const remaining = Math.max(0, safeAvailable - startIndex);
    const renderCount = Math.min(remaining, safeCapacity);

    if (renderCount === 0) return { startIndex, renderCount: 0, cols: 1, rows: 1 };

    let best = { cols: 1, rows: renderCount, score: Infinity };

    for (let c = 1; c <= maxCols; c++) {
      const r = Math.ceil(renderCount / c);
      if (r > maxRows) continue;

      const score = Math.abs(c - r);
      if (score < best.score) best = { cols: c, rows: r, score };
    }

    return { startIndex, renderCount, cols: best.cols, rows: best.rows };
  }, [availablePanels, capacity, page, maxCols, maxRows]);

  const slotIndices = useMemo(() => {
    return Array.from({ length: renderCount }, (_, i) => startIndex + i);
  }, [renderCount, startIndex]);

  const showNav = pageCount > 1;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {showNav && (
        <TopBar sx={{ alignItem: "center" }}>
          <IconButton aria-label="Vorherige Seite" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page <= 0}>
            <ChevronLeftIcon />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 1 }}>
            <div>
              Seite {page + 1}/{pageCount}
            </div>
          </Box>

          <IconButton aria-label="Nächste Seite" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}>
            <ChevronRightIcon />
          </IconButton>
        </TopBar>
      )}

      <Box
        ref={boxRef}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          overflow: "hidden",
          background: "#eee",
          p: 2,
          gap: 2,
          gridTemplateColumns: `repeat(${cols}, minmax(${SLOT_MIN_WIDTH}px, 1fr))`,
          gridTemplateRows: `repeat(${rows}, minmax(${SLOT_MIN_HEIGHT}px, 1fr))`,
        }}
      >
        {slotIndices.map((index) => (
          <Slot key={index} index={index} />
        ))}
      </Box>
    </Box>
  );
}
